/**
 * Where on the call an evidence quote was said, so a verdict can link to that moment ("Agent at 01:48").
 *
 * The scorer stores evidence as `{quote, speaker}` with no time, so the quote is found again in the call's
 * own SpeechSegments. It has to be matched the way the scorer saw the words, which is not quite the way the
 * page shows them:
 * - The scorer quotes `TranslatedText` when a segment has one, else `DisplayText`
 *   (pca-server/src/pca/pca-aws-fetch-transcript.py), so both are searched.
 * - Its transcript had filler words removed (`filler_remover` in the same file: um, uh, like, mhm), so they
 *   are dropped on both sides here.
 * - Redaction tags such as [PII] are dropped on both sides; a quote may or may not carry them.
 * - The same words can be said twice ("cancel my policy" by the customer, then read back by the agent), so
 *   among exact matches the one on the side of the speaker the scorer named wins.
 * A quote that cannot be placed confidently returns null and simply shows without a time: a link to the
 * wrong moment is worse than no link.
 */

const FILLER = new Set(['um', 'uh', 'like', 'mhm']);
const TAG = /^\[[A-Za-z_]+\]$/;          // [PII], [NAME] … — Transcribe's redaction tags
const MIN_WORDS = 3;                     // shorter quotes are too ambiguous to place
const FUZZY_MIN = 0.85;                  // stricter than the scorer's own 0.6: a wrong moment is worse than none

/** One word as the scorer and the transcript are compared: lower case, letters and digits only. */
export const normWord = (raw) => String(raw ?? '').toLowerCase().replace(/[^a-z0-9À-ɏ]+/g, '');

/** Spoken words only: lowercased, punctuation gone, filler and redaction tags dropped. */
export const wordsOf = (text) => String(text || '')
  .split(/\s+/)
  .filter((raw) => raw && !TAG.test(raw.replace(/[.,!?;:]+$/, '')))
  .map(normWord)
  .filter((w) => w && !FILLER.has(w));

// A number, or a numeric string; null, '' and anything else is no time at all (Number(null) would be 0).
const finite = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

/** The segment's words with a time each: WordConfidence's own times when they describe the text shown. */
const timedWords = (segment, segIndex) => {
  const start = finite(segment.SegmentStartTime) ?? 0;
  const end = finite(segment.SegmentEndTime) ?? start;
  const shown = wordsOf(segment.DisplayText);
  const confidence = Array.isArray(segment.WordConfidence) ? segment.WordConfidence : [];
  const fromConfidence = confidence
    .filter((w) => w && w.Text && !TAG.test(String(w.Text).replace(/[.,!?;:]+$/, '')))
    .map((w) => ({ word: normWord(String(w.Text)), time: finite(w.StartTime) }))
    .filter((w) => w.word && !FILLER.has(w.word));
  // Word times are trusted only when the words are the ones shown (an edited segment keeps its old words)
  // and only when they fall inside the segment (one legacy path fills every time with 0.0).
  const sameWords = fromConfidence.length === shown.length && fromConfidence.every((w, i) => w.word === shown[i]);
  const inSegment = (t) => t !== null && t >= start - 1 && t <= end + 1;
  if (sameWords) {
    return fromConfidence.map((w) => ({ w: w.word, seg: segIndex, time: inSegment(w.time) ? w.time : start }));
  }
  return shown.map((w) => ({ w, seg: segIndex, time: start }));
};

/** One word stream for the whole call (so a quote may cross a segment boundary), plus its translation. */
export const buildTranscriptIndex = (segments) => {
  const list = Array.isArray(segments) ? segments : [];
  const text = [];
  const translation = [];
  list.forEach((segment, i) => {
    if (!segment) return;
    text.push(...timedWords(segment, i));
    if (segment.TranslatedText) {
      const start = finite(segment.SegmentStartTime) ?? 0;
      wordsOf(segment.TranslatedText).forEach((w) => translation.push({ w, seg: i, time: start }));
    }
  });
  return { text, translation, segments: list };
};

// Every position where the words appear in order; usually one.
const findExact = (stream, words) => {
  const n = words.length;
  const hits = [];
  for (let i = 0; i + n <= stream.length; i += 1) {
    let k = 0;
    while (k < n && stream[i + k].w === words[k]) k += 1;
    if (k === n) hits.push(i);
  }
  return hits;
};

// The best window of the quote's length by shared words (counted with repeats), at least FUZZY_MIN of them.
// Returns the position of the window's first word that is in the quote, so the link starts on spoken words.
const findFuzzy = (stream, words) => {
  const n = words.length;
  if (stream.length < n) return -1;
  const want = new Map();
  words.forEach((w) => want.set(w, (want.get(w) || 0) + 1));
  const have = new Map();
  let shared = 0;
  const add = (w) => { const c = (have.get(w) || 0) + 1; have.set(w, c); if (c <= (want.get(w) || 0)) shared += 1; };
  const drop = (w) => { const c = have.get(w) || 0; if (c <= (want.get(w) || 0)) shared -= 1; have.set(w, c - 1); };
  let best = -1;
  let bestShared = 0;
  for (let i = 0; i < stream.length; i += 1) {
    add(stream[i].w);
    if (i >= n) drop(stream[i - n].w);
    if (i >= n - 1 && shared > bestShared) { bestShared = shared; best = i - n + 1; }
  }
  if (best < 0 || bestShared / n < FUZZY_MIN) return -1;
  for (let i = best; i < best + n; i += 1) if (want.has(stream[i].w)) return i;
  return best;
};

/**
 * `{seg, time, source: 'text' | 'translation'}` for a quote, or null when it cannot be placed confidently.
 * `prefer(seg)`, when given, picks among several exact matches (the first it accepts, else the first).
 */
export const locateQuote = (quote, index, { prefer } = {}) => {
  if (!quote || !index) return null;
  const fragments = String(quote).split(/…|\.{3}/).map(wordsOf).filter((f) => f.length >= MIN_WORDS);
  if (!fragments.length) return null;
  const streams = [['text', index.text || []], ['translation', index.translation || []]];
  for (const [source, stream] of streams) {
    for (const words of fragments) {
      const hits = findExact(stream, words);
      if (hits.length) {
        const at = (prefer && hits.find((i) => prefer(stream[i].seg))) ?? hits[0];
        return { seg: stream[at].seg, time: stream[at].time, source };
      }
    }
  }
  const longest = fragments.reduce((a, b) => (b.length > a.length ? b : a));
  for (const [source, stream] of streams) {
    const at = findFuzzy(stream, longest);
    if (at >= 0) return { seg: stream[at].seg, time: stream[at].time, source };
  }
  return null;
};

// The scorer names the speaker AGENT / CUSTOMER (older data Agent / Customer); the transcript puts agents on
// the right and everyone else on the left (common/speakers.js).
const SIDE_OF_SPEAKER = { agent: 'right', customer: 'left' };

// A cited quote's words (without the "…" that marks a cut), compared without case or spacing.
const bodyOf = (quote) => String(quote || '').trim().replace(/…$/, '').trim().toLowerCase().replace(/\s+/g, ' ');
const holds = (text, body) => Boolean(body) && String(text || '').toLowerCase().replace(/\s+/g, ' ').includes(body);

// Is the quote in the line's English rather than its own text? Then the link shows the English, so the words
// quoted are the ones on screen.
const onlyInTranslation = (quote, segment) => {
  const body = bodyOf(quote);
  return !holds(segment?.DisplayText, body) && holds(segment?.TranslatedText, body);
};

// How far `t` is from the line, 0 inside it, or null beyond the server's half-second either side
// (override_evidence._span_holds).
const distanceFrom = (t, s) => {
  const a = finite(s?.SegmentStartTime) ?? 0;
  const end = finite(s?.SegmentEndTime);
  const b = end !== null && end >= a ? end : a;
  if (t < a - 0.5 || t > b + 0.5) return null;
  return t >= a && t <= b ? 0 : Math.min(Math.abs(t - a), Math.abs(t - b));
};

/**
 * An evidence item's moment: its stored `start` when it has one (a reviewer's citation), else its quote's. A
 * citation names its line (`seg`), and links there when that line holds its words; without one, the nearest line
 * said at its time that holds them wins, as the server chose it, ties to the earlier line.
 * `sideOf(seg)` ('left' | 'right'), when given, prefers a match on the side of the item's own speaker.
 */
export const locateEvidence = (item, index, { sideOf } = {}) => {
  if (!item || !index) return null;
  const stored = finite(item.start);
  if (stored !== null) {
    const segs = index.segments || [];
    const body = bodyOf(item.quote);
    const holdsQuote = (s) => holds(s?.DisplayText, body) || holds(s?.TranslatedText, body);
    let seg = Number.isInteger(item.seg) && segs[item.seg] && holdsQuote(segs[item.seg]) ? item.seg : -1;
    if (seg < 0) {
      const near = segs.map((s, i) => ({ s, i, d: distanceFrom(stored, s) }))
        .filter((c) => c.d !== null)
        .sort((x, y) => x.d - y.d);
      seg = (near.find((c) => holdsQuote(c.s)) || near[0] || { i: -1 }).i;
    }
    if (seg < 0) {
      segs.forEach((s, i) => { if ((finite(s?.SegmentStartTime) ?? Infinity) <= stored) seg = i; });
    }
    if (seg < 0) return null;
    return { seg, time: stored, source: onlyInTranslation(item.quote, segs[seg]) ? 'translation' : 'stored' };
  }
  const side = SIDE_OF_SPEAKER[String(item.speaker || '').trim().toLowerCase()];
  const prefer = side && sideOf ? (seg) => sideOf(seg) === side : undefined;
  return locateQuote(item.quote, index, { prefer });
};
